import { computeSideBySidePairLayout } from '../game/tokenCellLayout.js';

/** facingSteps (est=0, sud=1, ouest=2, nord=3) → delta grille. */
export function facingStepsToForward(facingSteps: number): { dx: number; dy: number } {
  const s = ((facingSteps % 4) + 4) % 4;
  if (s === 0) return { dx: 1, dy: 0 };
  if (s === 1) return { dx: 0, dy: 1 };
  if (s === 2) return { dx: -1, dy: 0 };
  return { dx: 0, dy: -1 };
}

/** Emprise max d’un modèle 3D — longueur (demi-case, voir `computeVehicleModelScale`). */
export const LIVE_CAR_FOOTPRINT = 0.5;

/**
 * Largeur latérale à l'échelle de course (le modèle est calé sur sa *longueur* = 0.5).
 * Deux voitures taille normale se placent côte à côte sur la chaussée sans se recouvrir.
 */
export const LIVE_CAR_LATERAL_FOOTPRINT = 0.22;

/**
 * Jeu entre les flancs (fraction de case). Plus large que les pions 2D : en 3D,
 * un filet de 4 % disparaît dès que les voitures ne sont plus vues de face
 * (virage à 45°, caméra orbitale).
 */
export const LIVE_CAR_PAIR_EDGE_GAP = 0.16;

/**
 * Décalage dans le sens de la marche en virage (fraction de case). Sans ça,
 * les deux modèles restent au même theta de l’arc et s’empilent visuellement
 * même si leurs flancs sont disjoints sur le papier.
 */
export const LIVE_CAR_CORNER_STAGGER = 0.16;

/**
 * Demi-largeur de chaussée (fraction de case). Aligné sur la route procédurale
 * (`PROCEDURAL_ROAD_WIDTH` = 0.78) avec une petite marge de bordure.
 */
export const LIVE_CAR_ROAD_HALF_WIDTH = 0.36;

/**
 * Décalage latéral (fractions de case) pour voitures live 3D côte à côte.
 * `forward` doit être **partagé** entre tous les pairs de la case (sinon des
 * orientations différentes — typique en virage — recentrent les modèles).
 * Max 2 files : taille normale, emprises latérales disjointes, sur la chaussée.
 * En virage (tangente non cardinale), un léger décalage avant/arrière s’ajoute
 * pour que les carrosseries apparaissent l’une à côté de l’autre, pas empilées.
 */
export function computeLiveCarLaneOffset(
  forward: { dx: number; dy: number },
  stackIndex: number,
  peerCount: number,
  cellSize = 1
): { offsetX: number; offsetY: number; scale: number } {
  if (peerCount <= 1) {
    return { offsetX: 0, offsetY: 0, scale: 1 };
  }
  const layout = computeSideBySidePairLayout(
    forward,
    stackIndex,
    cellSize,
    LIVE_CAR_LATERAL_FOOTPRINT,
    LIVE_CAR_PAIR_EDGE_GAP
  );
  const isCorner = forward.dx !== 0 && forward.dy !== 0;
  if (!isCorner) {
    return layout;
  }
  const len = Math.hypot(forward.dx, forward.dy) || 1;
  const slotIndex = Math.min(Math.max(0, stackIndex), 1);
  const along = (slotIndex === 0 ? 1 : -1) * LIVE_CAR_CORNER_STAGGER * cellSize;
  return {
    offsetX: layout.offsetX + (forward.dx / len) * along,
    offsetY: layout.offsetY + (forward.dy / len) * along,
    scale: layout.scale,
  };
}

/** Demi-écart latéral réel d'une paire (pour aligner le marquage de grille). */
export const LIVE_CAR_PAIR_HALF_SPREAD = Math.abs(
  computeLiveCarLaneOffset({ dx: 1, dy: 0 }, 0, 2).offsetY
);
